import { createSimpleContext } from "@mux/ui/context"
import { createMemo, createRoot, onCleanup } from "solid-js"
import { useSessionParams } from "@/hooks/use-session-params"
import type { ReplyQuotePart } from "./reply-quote"
import type { FileSelection } from "@/context/file"
import { LocalDraft } from "@/storage/persist"
import { createPromptPersistence, type PromptPersistence } from "./prompt-persistence"


interface PartBase {
  content: string
  start: number
  end: number
}

export interface TextPart extends PartBase {
  type: "text"
}

export interface FileAttachmentPart extends PartBase {
  type: "file"
  path: string
  selection?: FileSelection
}

export interface AgentPart extends PartBase {
  type: "agent"
  name: string
}

export interface SessionAttachmentPart extends PartBase {
  type: "session"
  sessionID: string
  workspaceID?: string
  title: string
}

export interface CapabilityPart extends PartBase {
  type: "capability"
  id: string
  kind: "skill" | "tool"
  title: string
  prompt: string
}

export interface ImageAttachmentPart {
  type: "image"
  id: string
  filename: string
  mime: string
  dataUrl: string
  sourceUrl?: string
}

export type ContentPart = TextPart | FileAttachmentPart | AgentPart | SessionAttachmentPart | CapabilityPart | ImageAttachmentPart | ReplyQuotePart
export type Prompt = ContentPart[]

export type FileContextItem = {
  type: "file"
  path: string
  selection?: FileSelection
}

export type ContextItem = FileContextItem

export const DEFAULT_PROMPT: Prompt = [{ type: "text", content: "", start: 0, end: 0 }]

function isSelectionEqual(a?: FileSelection, b?: FileSelection) {
  if (!a && !b) return true
  if (!a || !b) return false
  return (
    a.startLine === b.startLine && a.startChar === b.startChar && a.endLine === b.endLine && a.endChar === b.endChar
  )
}

function isPartEqual(partA: ContentPart, partB: ContentPart) {
  switch (partA.type) {
    case "text":
      return partB.type === "text" && partA.content === partB.content
    case "file":
      return partB.type === "file" && partA.path === partB.path && isSelectionEqual(partA.selection, partB.selection)
    case "agent":
      return partB.type === "agent" && partA.name === partB.name
    case "session":
      return partB.type === "session" && partA.sessionID === partB.sessionID
    case "capability":
      return partB.type === "capability" && partA.id === partB.id && partA.prompt === partB.prompt
    case "reply-quote":
      return partB.type === "reply-quote" && partA.sessionID === partB.sessionID && partA.messageID === partB.messageID && partA.partID === partB.partID && partA.text === partB.text
    case "image":
      return partB.type === "image" && partA.id === partB.id
  }
}

export function isPromptEqual(promptA: Prompt, promptB: Prompt): boolean {
  if (promptA.length !== promptB.length) return false
  for (let i = 0; i < promptA.length; i++) {
    if (!isPartEqual(promptA[i], promptB[i])) return false
  }
  return true
}

function cloneSelection(selection?: FileSelection) {
  if (!selection) return undefined
  return { ...selection }
}

function clonePart(part: ContentPart): ContentPart {
  if (part.type === "text") return { ...part }
  if (part.type === "image" || part.type === "reply-quote") return { ...part }
  if (part.type === "agent") return { ...part }
  if (part.type === "session") return { ...part }
  if (part.type === "capability") return { ...part }
  return {
    ...part,
    selection: cloneSelection(part.selection),
  }
}

function clonePrompt(prompt: Prompt): Prompt {
  return prompt.map(clonePart)
}

function contextItemKey(item: ContextItem) {
  if (item.type !== "file") return item.type
  const start = item.selection?.startLine
  const end = item.selection?.endLine
  return `${item.type}:${item.path}:${start}:${end}`
}

const WORKSPACE_KEY = "__workspace__"
const MAX_PROMPT_SESSIONS = 20

type PromptSession = ReturnType<typeof createPromptSession>

type Scope = {
  dir: string
  id?: string
}

type PromptCacheEntry = {
  value: PromptSession
  dispose: VoidFunction
}

function createPromptSession(dir: string, id: string | undefined, persistence: PromptPersistence) {
  const target = LocalDraft.scoped(dir, id, "prompt")
  const draft = persistence.draft(`${target.storage}:${target.key}`)
  onCleanup(draft.close)
  const { store } = draft
  return {
    ready: draft.ready,
    storage: {
      issue: draft.issue,
    },
    beginSubmission: draft.beginSubmission,
    current: createMemo(() => store.prompt),
    dirty: createMemo(() => !isPromptEqual(store.prompt, DEFAULT_PROMPT)),
    context: {
      items: createMemo(() => store.context.items),
      add(item: ContextItem) {
        const key = contextItemKey(item)
        if (store.context.items.find((x) => x.key === key)) return
        draft.setContext([...store.context.items, { key, ...item }])
      },
      remove(key: string) {
        draft.setContext(store.context.items.filter((x) => x.key !== key))
      },

    },
    set: (prompt: Prompt) => draft.set(clonePrompt(prompt)),
    reset: draft.reset,
  }
}

const { use: usePromptContext, tryUse: useParentPromptContext, provider: PromptProvider } = createSimpleContext({
  name: "Prompt",
  gate: false,
  init: () => {
    const params = useSessionParams()
    // A chat page or grid window nested in the dashboard shares its drafts.
    const parent: PromptPersistence | undefined = useParentPromptContext()?.drafts
    const persistence: PromptPersistence = parent ?? createPromptPersistence()
    const cache = new Map<string, PromptCacheEntry>()

    const disposeAll = () => {
      for (const entry of cache.values()) {
        entry.dispose()
      }
      cache.clear()
      if (!parent) persistence.dispose()
    }

    onCleanup(disposeAll)

    const prune = () => {
      while (cache.size > MAX_PROMPT_SESSIONS) {
        const first = cache.keys().next().value
        if (!first) return
        const entry = cache.get(first)
        entry?.dispose()
        cache.delete(first)
      }
    }

    const load = (dir: string, id: string | undefined) => {
      const key = `${dir}:${id ?? WORKSPACE_KEY}`
      const existing = cache.get(key)
      if (existing) {
        cache.delete(key)
        cache.set(key, existing)
        return existing.value
      }

      const entry = createRoot((dispose) => ({
        value: createPromptSession(dir, id, persistence),
        dispose,
      }))

      cache.set(key, entry)
      prune()
      return entry.value
    }

    const session = createMemo(() => {
      const dir = params.dir
      if (!dir) return undefined
      return load(dir, params.id)
    })
    const pick = (scope?: Scope) => (scope ? load(scope.dir, scope.id) : session())

    return {
      drafts: persistence,
      storage: (scope?: Scope) => pick(scope)?.storage,
      settled: persistence.settled,
      beginSubmission: (scope?: Scope) => pick(scope)?.beginSubmission(),
      prefetch: (scope: Scope) => {
        load(scope.dir, scope.id)
      },
      ready: (scope?: Scope) => pick(scope)?.ready() ?? false,
      current: (scope?: Scope) => pick(scope)?.current() ?? DEFAULT_PROMPT,
      dirty: (scope?: Scope) => pick(scope)?.dirty() ?? false,
      context: {
        items: (scope?: Scope) => pick(scope)?.context.items() ?? [],
        add: (item: ContextItem, scope?: Scope) => pick(scope)?.context.add(item),
        remove: (key: string, scope?: Scope) => pick(scope)?.context.remove(key),
      },
      set: (prompt: Prompt, scope?: Scope) => pick(scope)?.set(prompt),
      reset: (scope?: Scope) => pick(scope)?.reset(),
    }
  },
})

export { PromptProvider }

export function usePrompt(scope?: Scope | (() => Scope)) {
  const context = usePromptContext()
  const resolve = (local?: Scope) => {
    if (local) return local
    if (typeof scope === "function") return scope()
    return scope
  }
  return {
    storage: (localScope?: Scope) => context.storage(resolve(localScope)),
    settled: context.settled,
    beginSubmission: (localScope?: Scope) => context.beginSubmission(resolve(localScope)),
    prefetch: (localScope: Scope) => context.prefetch(localScope),
    ready: (localScope?: Scope) => context.ready(resolve(localScope)),
    current: (localScope?: Scope) => context.current(resolve(localScope)),
    dirty: (localScope?: Scope) => context.dirty(resolve(localScope)),
    context: {
      items: (localScope?: Scope) => context.context.items(resolve(localScope)),
      add: (item: ContextItem, localScope?: Scope) => context.context.add(item, resolve(localScope)),
      remove: (key: string, localScope?: Scope) => context.context.remove(key, resolve(localScope)),
    },
    set: (prompt: Prompt, localScope?: Scope) => context.set(prompt, resolve(localScope)),
    reset: (localScope?: Scope) => context.reset(resolve(localScope)),
  }
}
